import { Appointment, BookingConfirmation, PartialService, Service, createEmptyAppointment } from '../types';
import { dayjs } from '../utils/dayjs-config';

const RECENT_BOOKING_STORAGE_KEY = 'recentBooking';

export type RecentBookingSnapshot = {
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  customerPhoneNumber: string;
  employeeID: string;
  servicesList: PartialService[];
  appointmentTitle: string;
  appointmentDuration: number;
  billingAmount: number;
  bookedAt?: string;
  appointmentDate?: string;
  appointmentTime?: string;
  notes?: string;
};

const isRecentBookingSnapshot = (value: unknown): value is RecentBookingSnapshot => {
  if (!value || typeof value !== 'object') return false;
  const snapshot = value as RecentBookingSnapshot;
  return (
    typeof snapshot.customerFirstName === 'string' &&
    typeof snapshot.customerLastName === 'string' &&
    typeof snapshot.customerEmail === 'string' &&
    typeof snapshot.customerPhoneNumber === 'string' &&
    typeof snapshot.employeeID === 'string' &&
    Array.isArray(snapshot.servicesList) &&
    typeof snapshot.appointmentTitle === 'string' &&
    typeof snapshot.appointmentDuration === 'number' &&
    typeof snapshot.billingAmount === 'number' &&
    (snapshot.bookedAt === undefined || typeof snapshot.bookedAt === 'string') &&
    (snapshot.appointmentDate === undefined || typeof snapshot.appointmentDate === 'string') &&
    (snapshot.appointmentTime === undefined || typeof snapshot.appointmentTime === 'string')
  );
};

export const loadRecentBookingSnapshot = (): RecentBookingSnapshot | null => {
  try {
    const raw = window.localStorage.getItem(RECENT_BOOKING_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return isRecentBookingSnapshot(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Failed to read recent booking snapshot', error);
    return null;
  }
};

export const saveRecentBookingSnapshot = (snapshot: RecentBookingSnapshot): void => {
  try {
    window.localStorage.setItem(RECENT_BOOKING_STORAGE_KEY, JSON.stringify(snapshot));
  } catch (error) {
    console.warn('Failed to save recent booking snapshot', error);
  }
};

export const buildRecentBookingSnapshot = (booking: BookingConfirmation): RecentBookingSnapshot => ({
  customerFirstName: booking.customerFirstName,
  customerLastName: booking.customerLastName,
  customerEmail: booking.customerEmail,
  customerPhoneNumber: booking.customerPhoneNumber,
  employeeID: booking.employeeID,
  servicesList: booking.servicesList ?? [],
  appointmentTitle: booking.appointmentTitle,
  appointmentDuration: booking.appointmentDuration,
  billingAmount: booking.billingAmount,
  bookedAt: dayjs.tz().toISOString(),
  appointmentDate: booking.appointmentDate,
  appointmentTime: booking.appointmentTime,
  notes: booking.notes,
});

// Hide the rebook prompt when the last booking is within the past hour.
export const isWithinOneHourOfBooking = (
  snapshot: RecentBookingSnapshot | null,
  referenceTime = dayjs.tz()
): boolean => {
  if (!snapshot?.bookedAt) return false;
  const bookedAt = dayjs(snapshot.bookedAt);
  if (!bookedAt.isValid()) return false;
  return Math.abs(referenceTime.diff(bookedAt, 'hour', true)) < 1;
};

export const buildAppointmentFromSnapshot = (
  snapshot: RecentBookingSnapshot,
  services: Service[]
): Appointment => {
  const serviceById = new Map(services.map((service) => [service.id, service]));
  const resolvedServices = snapshot.servicesList.map((service) => {
    const liveService = serviceById.get(service.id);
    if (!liveService) return service;
    return {
      id: liveService.id,
      serviceTitle: liveService.title,
      serviceTime: liveService.time,
      serviceAmount: liveService.price,
    };
  });

  const appointmentDuration =
    resolvedServices.reduce((sum, service) => sum + service.serviceTime, 0) || snapshot.appointmentDuration;
  const billingAmount =
    resolvedServices.reduce((sum, service) => sum + service.serviceAmount, 0) || snapshot.billingAmount;
  const appointmentTitle =
    resolvedServices.map((service) => service.serviceTitle).join(', ') || snapshot.appointmentTitle;

  return createEmptyAppointment({
    employeeID: snapshot.employeeID,
    billingAmount,
    appointmentDuration,
    appointmentTitle,
    servicesList: resolvedServices,
    notes: snapshot.notes ?? '',
    customerFirstName: snapshot.customerFirstName,
    customerLastName: snapshot.customerLastName,
    customerEmail: snapshot.customerEmail,
    customerPhoneNumber: snapshot.customerPhoneNumber,
  });
};
